import { forwardRef } from 'react';

export interface LabelData {
  trackingId: string;
  senderName: string;
  senderCity: string;
  receiverName: string;
  receiverCity: string;
  service: string;
  weight: string;
  estimatedDelivery: string;
  createdAt: string;
}

interface Props {
  data: LabelData;
}

const ShippingLabel = forwardRef<HTMLDivElement, Props>(({ data }, ref) => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/track/${data.trackingId}`)}&format=png`;

  return (
    <div ref={ref} className="shipping-label-wrapper" style={{ display: 'none' }}>
      <style>{`
        @media print {
          body > * { display: none !important; }
          .shipping-label-wrapper {
            display: block !important;
            position: fixed !important;
            inset: 0 !important;
            background: white !important;
            z-index: 99999 !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .sl-page {
            width: 148mm;
            min-height: 210mm;
            margin: 10mm auto;
            padding: 8mm;
            font-family: 'Arial', sans-serif;
            border: 2px solid #000;
            box-sizing: border-box;
            background: white;
          }
          .sl-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 2px solid #000;
            padding-bottom: 6mm;
            margin-bottom: 6mm;
          }
          .sl-logo-name {
            font-size: 22pt;
            font-weight: 900;
            letter-spacing: -0.5px;
            color: #0d9488;
          }
          .sl-logo-sub {
            font-size: 8pt;
            color: #555;
            margin-top: 2px;
          }
          .sl-service-badge {
            font-size: 9pt;
            font-weight: 800;
            background: #0d9488;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }
          .sl-qr-section {
            display: flex;
            align-items: flex-start;
            gap: 8mm;
            margin-bottom: 6mm;
          }
          .sl-qr-box {
            border: 1.5px solid #ccc;
            padding: 3mm;
            border-radius: 4px;
            flex-shrink: 0;
          }
          .sl-qr-box img { display: block; }
          .sl-tracking-block {
            flex: 1;
          }
          .sl-tracking-label {
            font-size: 7pt;
            font-weight: 700;
            color: #888;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 3mm;
          }
          .sl-tracking-id {
            font-family: 'Courier New', monospace;
            font-size: 16pt;
            font-weight: 900;
            letter-spacing: 1.5px;
            color: #000;
            word-break: break-all;
            margin-bottom: 4mm;
          }
          .sl-barcode {
            font-family: 'Courier New', monospace;
            font-size: 26pt;
            letter-spacing: 1px;
            color: #000;
            line-height: 1;
            margin-bottom: 1mm;
          }
          .sl-scan-hint {
            font-size: 7pt;
            color: #555;
            margin-top: 2mm;
          }
          .sl-divider {
            border: none;
            border-top: 1.5px dashed #bbb;
            margin: 5mm 0;
          }
          .sl-address-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6mm;
            margin-bottom: 6mm;
          }
          .sl-address-box {
            border: 1.5px solid #000;
            padding: 4mm;
            border-radius: 3px;
          }
          .sl-address-label {
            font-size: 7pt;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #888;
            margin-bottom: 2mm;
          }
          .sl-address-name {
            font-size: 11pt;
            font-weight: 700;
            color: #000;
          }
          .sl-address-city {
            font-size: 9pt;
            color: #444;
            margin-top: 1mm;
          }
          .sl-details-row {
            display: flex;
            gap: 4mm;
            margin-bottom: 4mm;
          }
          .sl-detail-box {
            flex: 1;
            border: 1px solid #ddd;
            border-radius: 3px;
            padding: 3mm 4mm;
          }
          .sl-detail-label {
            font-size: 7pt;
            color: #888;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 1mm;
          }
          .sl-detail-value {
            font-size: 10pt;
            font-weight: 700;
            color: #000;
          }
          .sl-footer {
            border-top: 1.5px solid #ccc;
            padding-top: 4mm;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .sl-footer-text {
            font-size: 7pt;
            color: #888;
          }
          .sl-priority-stamp {
            border: 2.5px solid #0d9488;
            color: #0d9488;
            font-size: 10pt;
            font-weight: 900;
            text-transform: uppercase;
            padding: 2mm 5mm;
            border-radius: 4px;
            letter-spacing: 1px;
          }
        }
      `}</style>

      <div className="sl-page">
        {/* Header */}
        <div className="sl-header">
          <div>
            <div className="sl-logo-name">SwiftTrack</div>
            <div className="sl-logo-sub">Global Logistics &amp; Courier Services</div>
          </div>
          <div className="sl-service-badge">{data.service}</div>
        </div>

        {/* QR + Tracking */}
        <div className="sl-qr-section">
          <div className="sl-qr-box">
            <img src={qrUrl} alt="QR Code" width={120} height={120} />
          </div>
          <div className="sl-tracking-block">
            <div className="sl-tracking-label">Tracking Number</div>
            <div className="sl-tracking-id">{data.trackingId}</div>
            <div className="sl-barcode">{'|'.repeat(22)}&nbsp;{'||'.repeat(5)}&nbsp;{'|'.repeat(14)}</div>
            <div className="sl-scan-hint">Scan QR code to track this shipment in real-time</div>
          </div>
        </div>

        <hr className="sl-divider" />

        {/* Addresses */}
        <div className="sl-address-grid">
          <div className="sl-address-box">
            <div className="sl-address-label">&#128195; From (Sender)</div>
            <div className="sl-address-name">{data.senderName}</div>
            <div className="sl-address-city">{data.senderCity}</div>
          </div>
          <div className="sl-address-box" style={{ borderWidth: '2px', borderColor: '#000' }}>
            <div className="sl-address-label">&#128205; To (Recipient)</div>
            <div className="sl-address-name">{data.receiverName}</div>
            <div className="sl-address-city">{data.receiverCity}</div>
          </div>
        </div>

        {/* Details row */}
        <div className="sl-details-row">
          <div className="sl-detail-box">
            <div className="sl-detail-label">Est. Delivery</div>
            <div className="sl-detail-value">{data.estimatedDelivery}</div>
          </div>
          <div className="sl-detail-box">
            <div className="sl-detail-label">Weight</div>
            <div className="sl-detail-value">{data.weight || '—'} kg</div>
          </div>
          <div className="sl-detail-box">
            <div className="sl-detail-label">Carrier</div>
            <div className="sl-detail-value">SwiftTrack</div>
          </div>
          <div className="sl-detail-box">
            <div className="sl-detail-label">Label Date</div>
            <div className="sl-detail-value">{data.createdAt}</div>
          </div>
        </div>

        {/* Footer */}
        <div className="sl-footer">
          <div className="sl-footer-text">
            <div>support@swifttrack.com · 1-800-SWIFT-00</div>
            <div style={{ marginTop: '2px' }}>This label was auto-generated by SwiftTrack admin portal</div>
          </div>
          <div className="sl-priority-stamp">{data.service}</div>
        </div>
      </div>
    </div>
  );
});

ShippingLabel.displayName = 'ShippingLabel';
export default ShippingLabel;
